import { UserIcon, UsersIcon, HeartIcon, ArrowRightIcon } from '@heroicons/react/24/outline';

const stages = [
  {
    icon: UserIcon,
    age: 'In your 50s or 60s',
    title: 'Start building the foundation',
    description: 'You are independent and active. SeniorThrive helps you check your home for risks early, build healthy habits, and set a baseline, so you are ahead of the curve, not reacting to it.',
    color: 'primary',
  },
  {
    icon: UsersIcon,
    age: 'In your 70s or 80s',
    title: 'Bring your family in when it feels right',
    description: 'You are still in charge, but maybe a family member wants to stay connected. Share your ThriveScore and daily signals, on your terms, so they can relax and you can stay independent.',
    color: 'emerald',
  },
  {
    icon: HeartIcon,
    age: 'When you need more support',
    title: 'Add caregiving without starting over',
    description: 'If professional care becomes part of your life, everything is already in place. Your history, your preferences, your care team, all in one system, growing with you.',
    color: 'amber',
  },
];

const RightForMeSection = () => {
  const colors = {
    primary: { bg: 'bg-primary/10', icon: 'text-primary' },
    emerald: { bg: 'bg-emerald-100 dark:bg-emerald-900/30', icon: 'text-emerald-600 dark:text-emerald-400' },
    amber: { bg: 'bg-amber-100 dark:bg-amber-900/30', icon: 'text-amber-600 dark:text-amber-400' },
  };

  return (
    <section className="py-20 md:py-28 bg-gradient-to-b from-background via-muted/20 to-background">
      <div className="container mx-auto px-4">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-14">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold text-foreground mb-6 leading-tight">
              "Is This Really for Me?"
            </h2>
            <p className="text-xl text-muted-foreground leading-relaxed max-w-3xl mx-auto">
              If you live at home and want to keep it that way, yes. SeniorThrive grows
              with you, wherever you are in your journey. The earlier you start, the
              stronger your foundation.
            </p>
          </div>

          {/* Journey stages */}
          <div className="relative">
            {/* Connecting line */}
            <div className="hidden md:block absolute top-1/2 left-[5%] right-[5%] h-px bg-gradient-to-r from-primary/20 via-emerald-400/20 to-amber-400/20 -translate-y-1/2" />

            <div className="grid md:grid-cols-3 gap-8">
              {stages.map((stage, i) => {
                const style = colors[stage.color as keyof typeof colors];
                return (
                  <div key={i} className={`relative z-10 bg-card rounded-md p-8 shadow-raised hover:shadow-floating hover:-translate-y-0.5 transition-all duration-300`}>
                    <div className={`w-12 h-12 ${style.bg} rounded-xl flex items-center justify-center mb-4`}>
                      <stage.icon className={`w-6 h-6 ${style.icon}`} />
                    </div>
                    <p className={`text-sm font-bold ${style.icon} uppercase tracking-wider mb-2`}>
                      {stage.age}
                    </p>
                    <h3 className="text-xl font-bold text-foreground mb-3">
                      {stage.title}
                    </h3>
                    <p className="text-muted-foreground leading-relaxed">
                      {stage.description}
                    </p>
                    {i < stages.length - 1 && (
                      <div className="hidden md:flex absolute -right-4 top-1/2 -translate-y-1/2 z-20 w-8 h-8 bg-card rounded-full items-center justify-center shadow-raised">
                        <ArrowRightIcon className="w-6 h-6 text-muted-foreground" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Reassurance */}
          <div className="mt-12 text-center">
            <p className="text-lg text-muted-foreground leading-relaxed max-w-2xl mx-auto">
              We have had people in their 50s start building habits and people in their 90s
              discover new ways to stay connected. There is no wrong time, only your time.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default RightForMeSection;
